import React from 'react';
import { useTranslation } from 'react-i18next';

function BusinessOutlookPage({ businessOutlookContent }) {
    const { t } = useTranslation();

    // 确保 businessOutlookContent 及其嵌套对象存在，否则使用空对象作为默认值
    const businessScopeItems = businessOutlookContent?.businessScopeItems || [];
    const businessExpansionItems = businessOutlookContent?.businessExpansionItems || [];
    const financialForecast = businessOutlookContent?.financialForecast || {};
    const revenueTable = financialForecast.revenueTable || [];
    const costAssumptions = financialForecast.costAssumptions || [];

    return (
        <section id="business-outlook" className="py-16 md:py-24 bg-gray-100 rounded-lg shadow-md m-4">
            <div className="container mx-auto px-4 md:px-6">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-10 section-title">{t('business_outlook_title')}</h2>
                <div className="bg-white p-8 rounded-lg shadow-lg">
                    {/* 业务范围 */}
                    <div className="mb-8">
                        <h3 className="text-2xl font-semibold text-gray-800 mb-4">{t('admin_business_outlook_scope_title')}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {businessScopeItems.map((item, index) => (
                                <div key={index} className="bg-gray-50 p-6 rounded-lg shadow-md">
                                    <h4 className="text-xl font-semibold text-gray-800 mb-3">{item.title}</h4>
                                    {item.text && <p className="text-gray-700 mb-3">{item.text}</p>}
                                    {item.points && item.points.length > 0 && (
                                        <ul className="list-disc list-inside text-gray-700 space-y-1">
                                            {item.points.map((point, pIndex) => (
                                                <li key={pIndex}>{point}</li>
                                            ))}
                                        </ul>
                                    )}
                                    {item.imageUrl && (
                                        <img src={item.imageUrl} alt={item.title} className="mt-4 w-full h-40 object-cover rounded-md" onError={(e) => e.target.src = 'https://placehold.co/300x200/cccccc/333333?text=Image+Error'} />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 业务拓展 */}
                    <div className="mb-8">
                        <h3 className="text-2xl font-semibold text-gray-800 mb-4">{t('admin_business_outlook_expansion_title')}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {businessExpansionItems.map((item, index) => (
                                <div key={index} className="bg-gray-50 p-6 rounded-lg shadow-md">
                                    <h4 className="text-xl font-semibold text-gray-800 mb-3">{item.title}</h4>
                                    {item.text && <p className="text-gray-700 mb-3">{item.text}</p>}
                                    {item.points && item.points.length > 0 && (
                                        <ul className="list-disc list-inside text-gray-700 space-y-1">
                                            {item.points.map((point, pIndex) => (
                                                <li key={pIndex}>{point}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 财务预测 */}
                    <div className="mb-8">
                        <h3 className="text-2xl font-semibold text-gray-800 mb-4">{t('admin_business_outlook_financial_revenue_title')}</h3>
                        <div className="overflow-x-auto">
                            <table className="min-w-full bg-white rounded-lg shadow-md">
                                <thead>
                                    <tr className="bg-blue-100 text-blue-800">
                                        <th className="py-2 px-3 text-left">{t('admin_business_outlook_year_column')}</th>
                                        <th className="py-2 px-3 text-left">{t('admin_business_outlook_revenue_column')}</th>
                                        <th className="py-2 px-3 text-left">{t('admin_business_outlook_sales_column')}</th>
                                        <th className="py-2 px-3 text-left">{t('admin_business_outlook_gross_profit_column')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {revenueTable.map((row, index) => (
                                        <tr key={index} className="border-b border-gray-200">
                                            <td className="py-2 px-3">{row.year}</td>
                                            <td className="py-2 px-3">{row.revenue}</td>
                                            <td className="py-2 px-3">{row.sales}</td>
                                            <td className="py-2 px-3">{row.grossProfit}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="mb-8">
                        <h3 className="text-2xl font-semibold text-gray-800 mb-4">{t('admin_business_outlook_financial_cost_title')}</h3>
                        <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                            {costAssumptions.map((assumption, index) => (
                                <li key={index}>
                                    <strong>{assumption.type}:</strong> {(assumption.details || []).join('; ')}
                                </li>
                            ))}
                        </ul>
                        <p className="text-gray-700">
                            <strong>{t('admin_business_outlook_investment_opportunity_label')}:</strong> {financialForecast.investmentOpportunity}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default BusinessOutlookPage;
